/**
 * Fortnite Shop Parser & Layout Engine
 * Ported directly from the original production elGordoFortnite shop engine.
 * Implements exact grid sizing, card ratios, rarity gradients, image crops,
 * and section ordering logic.
 */

export interface TextureMetadataItem {
  key: string;
  value: string;
}

export interface TextMetadataItem {
  key: string;
  value: string;
}

export interface MaterialInstanceColor {
  Background_Color_A?: string;
  Background_Color_B?: string;
  [key: string]: string | undefined;
}

export interface MaterialInstance {
  images?: Record<string, string>;
  colors?: MaterialInstanceColor;
}

export interface RenderImageItem {
  productTag?: string;
  image?: string;
}

export interface NewDisplayAsset {
  materialInstances?: MaterialInstance[];
  renderImages?: RenderImageItem[];
}

export interface CosmeticSubItem {
  id?: string;
  name?: string;
  title?: string;
  description?: string;
  artist?: string;
  albumArt?: string;
  image?: string;
  images?: {
    icon?: string;
    smallIcon?: string;
    featured?: string;
    large?: string;
    small?: string;
    [key: string]: string | undefined;
  };
  type?: {
    value?: string;
    displayValue?: string;
    backendValue?: string;
  };
  rarity?: {
    value?: string;
    displayValue?: string;
  };
  series?: {
    value?: string;
    displayValue?: string;
  } | null;
  set?: {
    value?: string;
    text?: string;
  };
  introduction?: {
    chapter?: string;
    season?: string;
    text?: string;
  };
}

export interface RawShopEntry {
  offerId: string;
  devName?: string;
  finalPrice: number;
  regularPrice?: number;
  tileSize?: string;
  size?: string;
  sortPriority?: number;
  outDate?: string;
  bundle?: {
    name?: string;
    info?: string;
    image?: string;
  };
  layout?: {
    id?: string;
    name?: string;
    category?: string;
    rank?: number;
    index?: number;
    displayType?: string;
    textureMetadata?: TextureMetadataItem[];
    textMetadata?: TextMetadataItem[];
  };
  layoutId?: string;
  section?: {
    name?: string;
  };
  brItems?: CosmeticSubItem[];
  cars?: CosmeticSubItem[];
  tracks?: CosmeticSubItem[];
  instruments?: CosmeticSubItem[];
  legoKits?: CosmeticSubItem[];
  items?: CosmeticSubItem[];
  newDisplayAsset?: NewDisplayAsset;
  newDisplayAssetPath?: string;
  displayAssetPath?: string;
  colors?: {
    color1?: string;
    color2?: string;
    color3?: string;
    Color1?: string;
    Color2?: string;
    Color3?: string;
    [key: string]: string | undefined;
  };
}

export interface ParsedShopItem {
  name: string;
  description: string | null;
  image: string;
  bgImage?: string | null;
  fgImage?: string | null;
  bodyImage?: string | null;
  titleImage?: string | null;
  rarity: {
    value: string;
    displayValue: string;
  };
  series: {
    value?: string;
    displayValue?: string;
  } | null;
  type: {
    value?: string;
    displayValue?: string;
    backendValue?: string;
  } | null;
  isBundle: boolean;
  isBillboard: boolean;
}

// Memory cache for weakmap
const parsedItemCache = new WeakMap<RawShopEntry, ParsedShopItem | null>();

/**
 * Parses a raw shop entry to find primary item images, bundle metadata and billboards
 */
export function parseRawEntryItem(entry: RawShopEntry): ParsedShopItem | null {
  if (!entry || typeof entry !== 'object') return null;
  if (parsedItemCache.has(entry)) return parsedItemCache.get(entry)!;

  const isBillboard = entry.layout?.displayType === 'billboard';
  const textures = entry.layout?.textureMetadata || [];
  const bgTexture = textures.find((t) => t.key === 'background')?.value;
  const fgTexture =
    textures.find((t) => t.key === 'foreground')?.value ||
    textures.find((t) => t.key === 'foreground_juno')?.value;
  const bodyImage = textures.find((t) => t.key === 'bodyImage')?.value;
  const titleImage = textures.find((t) => t.key === 'titleImage')?.value;

  const texts = entry.layout?.textMetadata || [];
  const titleText = texts.find((t) => t.key === 'title')?.value;
  const subtitleText = texts.find((t) => t.key === 'subtitle')?.value;

  const isCar =
    (entry.cars && entry.cars.length > 0) ||
    entry.layout?.id?.toLowerCase().includes('car') ||
    entry.layout?.category?.toLowerCase().includes('motores');

  const carImage =
    entry.cars && entry.cars.length > 0
      ? entry.cars[0]?.images?.large || entry.cars[0]?.images?.small
      : null;

  const renderImages = entry.newDisplayAsset?.renderImages;
  const primaryRenderImage =
    renderImages?.find((img) => img && img.image && img.productTag !== 'Product.Juno')?.image ||
    (renderImages && renderImages.length > 0 ? renderImages[0].image : null);

  const materialInstances = entry.newDisplayAsset?.materialInstances;
  const materialImages =
    materialInstances && materialInstances.length > 0 ? materialInstances[0].images : null;
  const offerImage = materialImages?.OfferImage || materialImages?.Background;

  const subItems = [
    ...(entry.brItems || []),
    ...(entry.cars || []),
    ...(entry.tracks || []),
    ...(entry.instruments || []),
    ...(entry.legoKits || []),
    ...(entry.items || []),
  ];
  const firstSubItem = subItems[0];
  const subImages = firstSubItem?.images || {};
  const fallbackSubImage =
    subImages.featured ||
    subImages.large ||
    subImages.icon ||
    subImages.small ||
    firstSubItem?.albumArt ||
    firstSubItem?.image;

  const bundleNameLower = (entry.bundle?.name || '').toLowerCase();
  const devNameLower = (entry.devName || '').toLowerCase();

  const isBundle =
    (Boolean(entry.bundle) &&
      (bundleNameLower.includes('lote') ||
        bundleNameLower.includes('bundle') ||
        bundleNameLower.includes('paquete') ||
        (entry.brItems &&
          entry.brItems.length > 1 &&
          !entry.brItems.every(
            (it) => it.type?.value === 'outfit' || it.type?.value === 'backpack'
          )))) ||
    Boolean(entry.layout?.category && entry.layout.category.toLowerCase().includes('lote')) ||
    isBillboard ||
    Boolean(entry.tileSize?.startsWith('Size_4_x')) ||
    Boolean(
      subtitleText &&
        (subtitleText.toLowerCase().includes('lote') || subtitleText.toLowerCase().includes('bundle'))
    ) ||
    Boolean(
      entry.newDisplayAssetPath && entry.newDisplayAssetPath.toLowerCase().includes('bundle')
    ) ||
    Boolean(entry.displayAssetPath && entry.displayAssetPath.toLowerCase().includes('bundle')) ||
    devNameLower.includes('bundle');

  let resolvedImage: string | null = null;

  if (isBillboard && (fgTexture || bgTexture)) {
    resolvedImage = fgTexture || bgTexture || null;
  } else if (isCar) {
    resolvedImage = primaryRenderImage || entry.bundle?.image || offerImage || carImage || fallbackSubImage || null;
  } else if (isBundle) {
    resolvedImage =
      fgTexture ||
      offerImage ||
      primaryRenderImage ||
      entry.bundle?.image ||
      firstSubItem?.images?.featured ||
      firstSubItem?.images?.large ||
      firstSubItem?.images?.icon ||
      fallbackSubImage ||
      null;
  } else {
    resolvedImage =
      offerImage || primaryRenderImage || fallbackSubImage || entry.bundle?.image || fgTexture || null;
  }

  if (!resolvedImage && !firstSubItem && !isBillboard) {
    parsedItemCache.set(entry, null);
    return null;
  }

  if (isBundle) {
    let resolvedName = entry.bundle?.name;
    if (!resolvedName && titleText) {
      const clean = titleText.trim();
      resolvedName = clean.toLowerCase().startsWith('lote') ? clean : `Lote de ${clean}`;
    }
    if (!resolvedName && entry.layout?.name && (isBillboard || entry.tileSize?.startsWith('Size_4_x'))) {
      const clean = entry.layout.name.trim();
      resolvedName = clean.toLowerCase().startsWith('lote') ? clean : `Lote de ${clean}`;
    }
    if (!resolvedName) {
      resolvedName = firstSubItem?.name || 'Lote Especial';
    }

    let resolvedDesc = entry.bundle?.info;
    if (
      resolvedDesc &&
      (resolvedDesc.toLowerCase() === 'bundle' ||
        resolvedDesc.toLowerCase() === 'conjunto de objetos' ||
        resolvedDesc.toLowerCase() === 'lote')
    ) {
      resolvedDesc = undefined;
    }
    if (!resolvedDesc && subtitleText && subtitleText.toLowerCase() !== 'bundle') {
      resolvedDesc = subtitleText;
    }

    const result: ParsedShopItem = {
      name: resolvedName,
      description: resolvedDesc || null,
      image: resolvedImage || 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png',
      bgImage: bgTexture || null,
      fgImage: fgTexture || null,
      bodyImage: bodyImage || null,
      titleImage: titleImage || null,
      rarity: {
        value: firstSubItem?.rarity?.value || 'epic',
        displayValue: firstSubItem?.rarity?.displayValue || 'Lote',
      },
      series: firstSubItem?.series || null,
      type: firstSubItem?.type || { value: 'bundle', displayValue: 'Lote' },
      isBundle: true,
      isBillboard,
    };
    parsedItemCache.set(entry, result);
    return result;
  }

  const result: ParsedShopItem = {
    name: firstSubItem?.name || firstSubItem?.title || entry.bundle?.name || 'Cosmético',
    description:
      firstSubItem?.description ||
      firstSubItem?.artist ||
      (entry.bundle?.info && entry.bundle.info.toLowerCase() !== 'bundle' ? entry.bundle.info : null),
    image: resolvedImage || 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png',
    rarity: {
      value: firstSubItem?.rarity?.value || 'common',
      displayValue: firstSubItem?.rarity?.displayValue || 'Común',
    },
    series: firstSubItem?.series || null,
    type: firstSubItem?.type || null,
    isBundle: false,
    isBillboard: false,
  };

  parsedItemCache.set(entry, result);
  return result;
}

/**
 * Calculates column span size (1, 2, 3, or 4) based on official layout & tileSize
 */
export function calculateColumnSpan(entry?: RawShopEntry | null): number {
  if (!entry) return 1;
  if (entry.layout?.displayType === 'billboard' || entry.tileSize === 'Size_4_x_1' || entry.tileSize === 'Size_4_x_2') {
    return 4;
  }
  if (entry.tileSize === 'Size_3_x_1') return 3;
  if (entry.tileSize === 'Size_2_x_1' || entry.tileSize === 'Size_2_x_2') return 2;
  if (entry.tileSize === 'Size_1_x_1' || entry.tileSize === 'Size_1_x_2') return 1;
  if (entry.bundle) return 2;

  const descriptor = [entry.layout?.id, entry.layout?.name, entry.size]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (descriptor.includes('quadwide') || descriptor.includes('span-4')) return 4;
  if (descriptor.includes('triplewide') || descriptor.includes('span-3')) return 3;
  if (descriptor.includes('doublewide') || descriptor.includes('span-2') || descriptor.includes('doble')) return 2;

  if (entry.cars && entry.cars.length > 0) {
    if (entry.cars.length === 1 && entry.cars[0]?.type?.value === 'wheel') return 1;
    return 2;
  }

  return 1;
}

/** Helper to darken a hex color */
function darkenHex(hex: string, factor = 0.4): string {
  try {
    const s = (hex || '').replace('#', '').trim().substring(0, 6);
    if (s.length < 6) return '1a1a1e';
    const r = Math.max(0, Math.min(255, Math.round(parseInt(s.substring(0, 2), 16) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(parseInt(s.substring(2, 4), 16) * factor)));
    const b = Math.max(0, Math.min(255, Math.round(parseInt(s.substring(4, 6), 16) * factor)));
    return `${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch {
    return '1a1a1e';
  }
}

/** Helper to calculate color luminance */
function getLuminance(hex: string): number {
  if (!hex || typeof hex !== 'string') return 0;
  const a = hex.replace('#', '').trim().substring(0, 6);
  if (a.length < 6) return 0;
  const r = parseInt(a.substring(0, 2), 16);
  const g = parseInt(a.substring(2, 4), 16);
  const b = parseInt(a.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return 0;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Calculates card background gradient matching the official Fortnite shop
 */
export function calculateCardBackground(entry?: RawShopEntry | null, item?: ParsedShopItem | null): string {
  if (!entry && !item) {
    return 'linear-gradient(180deg, #4b5563 0%, #374151 100%)';
  }

  // 1. Check entry.colors
  const c1 = entry?.colors?.color1 || entry?.colors?.Color1;
  const c3 = entry?.colors?.color3 || entry?.colors?.Color2;
  if (c1 && c3 && c1.length >= 6 && c3.length >= 6) {
    const f = c1.substring(0, 6);
    const v = c3.substring(0, 6);
    let p = v;
    if (getLuminance(v) > 0.52) p = darkenHex(v, 0.65);
    return `linear-gradient(180deg, #${f} 0%, #${p} 100%)`;
  }

  // 2. Check material instances colors
  const matColors = entry?.newDisplayAsset?.materialInstances?.[0]?.colors;
  if (matColors?.Background_Color_A && matColors?.Background_Color_B) {
    const f = matColors.Background_Color_A.substring(0, 6);
    const v = matColors.Background_Color_B.substring(0, 6);
    let p = v;
    if (getLuminance(v) > 0.52) p = darkenHex(v, 0.65);
    return `linear-gradient(180deg, #${f} 0%, #${p} 100%)`;
  }

  // 3. Fallback series & rarity gradients
  const seriesStr = (item?.series?.value || '').toLowerCase();
  const rarityStr = (item?.rarity?.value || item?.rarity?.displayValue || '').toLowerCase();
  const combined = `${seriesStr} ${rarityStr}`;

  if (combined.includes('gaminglegends') || combined.includes('videojuegos')) {
    return 'linear-gradient(180deg, #ffc700 0%, #ff8c00 100%)';
  }
  if (combined.includes('marvel')) {
    return 'linear-gradient(180deg, #ed1d24 0%, #a80e14 100%)';
  }
  if (combined.includes('dc')) {
    return 'linear-gradient(180deg, #0055ff 0%, #0033aa 100%)';
  }
  if (combined.includes('starwars')) {
    return 'linear-gradient(180deg, #3d5a80 0%, #1d2d44 100%)';
  }
  if (combined.includes('icon') || combined.includes('ídolos') || combined.includes('idolos')) {
    return 'linear-gradient(180deg, #2be4cb 0%, #159f8e 100%)';
  }
  if (combined.includes('legendary') || combined.includes('legendario')) {
    return 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)';
  }
  if (combined.includes('epic') || combined.includes('épico') || combined.includes('epico')) {
    return 'linear-gradient(180deg, #c026d3 0%, #9333ea 100%)';
  }
  if (combined.includes('rare') || combined.includes('raro')) {
    return 'linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%)';
  }
  if (combined.includes('uncommon') || combined.includes('poco')) {
    return 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)';
  }
  return 'linear-gradient(180deg, #4b5563 0%, #374151 100%)';
}

/**
 * Returns localized category label for sliding bottom tag bar in cards
 */
export function getSlidingCategoryTag(entry?: RawShopEntry | null, item?: ParsedShopItem | null): string {
  if (!entry && !item) return 'OBJETO';
  if (item?.isBillboard || entry?.bundle || item?.isBundle) return 'LOTE';

  if (
    (entry?.tracks && entry.tracks.length > 0) ||
    item?.type?.value === 'music' ||
    item?.type?.backendValue === 'AthenaMusicPack' ||
    entry?.layout?.name?.toLowerCase().includes('improvisación')
  ) {
    return 'PISTA DE IMPROVISACIÓN';
  }

  if (entry?.cars && entry.cars.length > 0) {
    const carType = entry.cars[0]?.type?.displayValue;
    return carType ? carType.toUpperCase() : 'VEHÍCULO';
  }

  const rawType = (item?.type?.displayValue || item?.type?.value || '').trim().toLowerCase();

  if (rawType === 'traje' || rawType === 'outfit' || item?.type?.backendValue === 'AthenaCharacter') {
    return 'ATUENDO';
  }
  if (rawType === 'accesorio mochilero' || rawType === 'mochila' || item?.type?.backendValue === 'AthenaBackpack') {
    return 'ACCESORIO MOCHILERO';
  }
  if (rawType === 'herramienta de recolección' || rawType === 'pico' || item?.type?.backendValue === 'AthenaPickaxe') {
    return 'PICO';
  }
  if (rawType === 'ala delta' || rawType === 'aladelta' || rawType === 'planeador' || item?.type?.backendValue === 'AthenaGlider') {
    return 'ALADELTA';
  }
  if (rawType === 'gesto' || rawType === 'baile' || item?.type?.backendValue === 'AthenaDance') {
    return 'GESTO';
  }
  if (rawType === 'envoltorio' || rawType === 'papel' || item?.type?.backendValue === 'AthenaItemWrap') {
    return 'PAPEL';
  }

  return rawType ? rawType.toUpperCase() : 'OBJETO';
}

/**
 * Checks whether entry leaves the shop in <= 24 hours
 */
export function isLeavingToday(entry: RawShopEntry): boolean {
  if (!entry || !entry.outDate) return false;
  try {
    const outTime = new Date(entry.outDate).getTime();
    const now = Date.now();
    const diffHours = (outTime - now) / (1000 * 60 * 60);
    return diffHours >= 0 && diffHours <= 24;
  } catch {
    return false;
  }
}

export interface BalancedEntry {
  entry: RawShopEntry;
  item: ParsedShopItem;
  isOrphan: boolean;
}

/**
 * Preserves exact authentic Fortnite item order without reordering or orphan stretching
 */
export function balanceSectionEntries(entries: RawShopEntry[]): BalancedEntry[] {
  return entries
    .map((entry) => {
      const item = parseRawEntryItem(entry);
      if (!item) return null;
      return { entry, item, isOrphan: false };
    })
    .filter(Boolean) as BalancedEntry[];
}

/**
 * Extracts numeric suffix from layoutId (e.g. "FNM102826.98" -> 98)
 * Direct port of `Pe` from official production tienda bundle.
 */
export function extractLayoutIndexSuffix(entry?: RawShopEntry | null): number {
  if (!entry) return 0;
  const layoutId = entry.layoutId || '';
  const dotIdx = layoutId.lastIndexOf('.');
  if (dotIdx !== -1) {
    const parsed = parseInt(layoutId.substring(dotIdx + 1), 10);
    if (!isNaN(parsed)) return parsed;
  }
  return 0;
}

/**
 * Groups and sorts shop entries into authentic Fortnite sections
 * Mantiene la regla oficial de rank descendente y envía "Pistas de improvisación" al final.
 */
export function groupAndOrderShopSections(rawEntries: RawShopEntry[]): [string, RawShopEntry[]][] {
  const uniqueEntriesMap = new Map<string, RawShopEntry>();

  for (const entry of rawEntries) {
    const hasLayout = Boolean(entry.layout && (entry.layout.name || entry.layout.category));
    const isAlc = entry.layoutId?.toLowerCase().startsWith('alc');
    if (hasLayout && !isAlc && parseRawEntryItem(entry) !== null) {
      uniqueEntriesMap.set(entry.offerId, entry);
    }
  }

  const sectionsMap: Record<string, RawShopEntry[]> = {};
  for (const entry of uniqueEntriesMap.values()) {
    const secName =
      entry.layout?.name ||
      entry.layout?.category ||
      entry.section?.name ||
      'Destacados';
    if (!sectionsMap[secName]) {
      sectionsMap[secName] = [];
    }
    sectionsMap[secName].push(entry);
  }

  // Sort inside each section matching exact production Nt algorithm:
  // 1. Descending layoutId numeric suffix: Pe(h) - Pe(i)
  // 2. Descending sortPriority: (h.sortPriority ?? -999) - (i.sortPriority ?? -999)
  // 3. Descending column span: (ve(h) || 1) - (ve(i) || 1)
  for (const secName of Object.keys(sectionsMap)) {
    sectionsMap[secName].sort((i, h) => {
      const f = extractLayoutIndexSuffix(i);
      const v = extractLayoutIndexSuffix(h);
      if (v !== f) return v - f;

      const p = i.sortPriority != null ? i.sortPriority : -999;
      const c = h.sortPriority != null ? h.sortPriority : -999;
      if (c !== p) return c - p;

      return (calculateColumnSpan(h) || 1) - (calculateColumnSpan(i) || 1);
    });
  }

  // Sort sections by official rank, sending Jam Tracks to the absolute bottom
  return Object.entries(sectionsMap).sort(([nameA, itemsA], [nameB, itemsB]) => {
    const isJamA =
      nameA.toLowerCase().includes('improvisación') ||
      itemsA[0]?.layout?.displayType === 'expandableList';
    const isJamB =
      nameB.toLowerCase().includes('improvisación') ||
      itemsB[0]?.layout?.displayType === 'expandableList';

    if (isJamA && !isJamB) return 1;
    if (!isJamA && isJamB) return -1;

    const rankA = Math.max(...itemsA.map((it) => it.layout?.rank ?? 0));
    const rankB = Math.max(...itemsB.map((it) => it.layout?.rank ?? 0));
    if (rankB !== rankA) return rankB - rankA;

    const idxA = Math.max(...itemsA.map((it) => it.layout?.index ?? 0));
    const idxB = Math.max(...itemsB.map((it) => it.layout?.index ?? 0));
    return idxB - idxA;
  });
}
